import { useSelector } from 'react-redux'
import { format } from 'date-fns'
import { useFormik } from 'formik'

import { reportsSelector } from '@/state/reports/slices/index.slices'
import {
  GlobalReportData,
  GlobalReportLibraryColumnProps,
  ReportsFilters,
  RoundsReportData,
  RoundsReportLibraryColumnProps,
} from '@/state/reports/models'
import { invertedNormalDatePaymentFormat } from '@/constants/date'
import { ApplicationState } from '@/state/root.models'
import { ADMIN_VIEW } from '@/constants/brand'

import { TableHeadCellPropsType } from '@/state/ui/global/models'
import useReportsActions from './use-reports-actions.hooks'
import { initialValuesReports, validationSchemaReports } from './schema'

const useReportsFilters = () => {
  const {
    // getAllGlobalReports,
    getGlobalReports,
    getRoundsReports,
    setReportsFilters,
    resetGlobalReports,
    resetRoundsReports,
  } = useReportsActions()

  const { filters, roundsPagination, globalPagination } =
    useSelector(reportsSelector)

  const { brand } = useSelector(({ auth }: ApplicationState) => auth)

  const formikReports = useFormik({
    initialValues: initialValuesReports,
    validationSchema: validationSchemaReports,
    onSubmit: () => {},
    enableReinitialize: true,
  })

  const buildFiltersByReport = (
    payload: ReportsFilters,
    headCells?:
      | TableHeadCellPropsType<RoundsReportLibraryColumnProps>[]
      | TableHeadCellPropsType<GlobalReportLibraryColumnProps>[],
    fileName?: string,
    getFormattedRow?: (
      row: RoundsReportData | GlobalReportData,
    ) => RoundsReportLibraryColumnProps | GlobalReportLibraryColumnProps,
  ) => {
    if (!payload.reportType || !payload.startDate || !payload.endDate) {
      return
    }

    const filter: any = {
      transactionsDateRange: '',
    }
    Object.keys(payload).forEach((key) => {
      if (
        key === 'gameCategory' &&
        payload.gameCategory &&
        payload.gameCategory.length > 0
      ) {
        filter[key] = payload.gameCategory
          ?.map((code: string) => code.toLowerCase())
          .join(',')
          .toString()
      } else if (
        key === 'gameProvider' &&
        payload.gameProvider &&
        payload.gameProvider.length > 0
      ) {
        filter[key] = payload.gameProvider
          .map((provider: any) => provider.name)
          ?.join(',')
          .toString()
      } else if (
        key === 'game' &&
        payload.game !== '' &&
        payload.game !== null &&
        payload.game.length > 0
      ) {
        filter[key] = payload.game?.join(',').toString()
      } else if (key === 'startDate' && payload.startDate) {
        const newStartDate = new Date(payload.startDate?.toString())
        if (!isNaN(newStartDate.getTime())) {
          const startDate = format(
            newStartDate,
            invertedNormalDatePaymentFormat,
          )
          filter['transactionsDateRange'] = startDate?.toString() // eslint-disable-line
        }
      } else if (key === 'endDate' && payload.endDate) {
        const newEndDate = new Date(payload.endDate?.toString())
        if (!isNaN(newEndDate.getTime())) {
          const endDate = format(newEndDate, invertedNormalDatePaymentFormat)
          // eslint-disable-next-line
          filter['transactionsDateRange'] =
            (filter['transactionsDateRange'] || '') + '?' + endDate?.toString()
        }
      } else if (key === 'reportType') {
        filter[key] = payload.reportType
        filter.page = payload.page
        filter.limit = payload.limit
      } else if (key === 'playerId') {
        filter[key] = payload.playerId
      } else if (key === 'username') {
        filter[key] = payload.username
      } else if (
        key === 'transactionTypePayment' &&
        payload.transactionTypePayment !== 'all'
      ) {
        filter[key] = payload.transactionTypePayment
      } else if (key === 'brandId' && brand.siteId === ADMIN_VIEW) {
        filter[key] = payload.brandId
      }
    })
    //HERE
    if (payload.reportType === '1') {
      if (!filter['brandId'] && brand.siteId !== ADMIN_VIEW) {
        filter['brandId'] = brand.siteId
      }

      getRoundsReports(
        {
          ...filter,
          xlsx: payload.xlsx,
        },
        headCells as TableHeadCellPropsType<RoundsReportLibraryColumnProps>[],
        fileName,
        getFormattedRow,
      )
    } else {
      if (!filter['brandId'] && brand.siteId !== ADMIN_VIEW) {
        filter['brandId'] = brand.siteId
      }

      getGlobalReports(
        {
          ...filter,
          xlsx: payload.xlsx,
        },
        headCells as TableHeadCellPropsType<GlobalReportLibraryColumnProps>[],
        fileName,
        getFormattedRow,
      )
    }
  }

  const downloadXlsxSearchFilter = (
    filters: ReportsFilters,
    headCells?:
      | TableHeadCellPropsType<RoundsReportLibraryColumnProps>[]
      | TableHeadCellPropsType<GlobalReportLibraryColumnProps>[],
    fileName?: string,
    getFormattedRow?: (
      row: RoundsReportData | GlobalReportData,
    ) => RoundsReportLibraryColumnProps | GlobalReportLibraryColumnProps,
  ) => {
    buildFiltersByReport(filters, headCells, fileName, getFormattedRow)
  }

  const submitSearchFilter = (customFilters?: ReportsFilters) => {
    buildFiltersByReport(customFilters || { ...filters })
  }

  const handleChangeSearch = (field: string, value: any) => {
    const newFilters = {
      ...filters,
      [field]: value,
      page: 1,
    } as ReportsFilters
    setReportsFilters(newFilters)
    if (field === 'reportType') {
      if (value === 1) {
        resetRoundsReports()
      } else {
        resetGlobalReports()
      }
    }

    formikReports.setFieldValue(field, value)
    return newFilters
  }

  const handleBulkChangeSearch = (newValues: Partial<ReportsFilters>) => {
    const newFilters = {
      ...filters,
      ...newValues,
      page: 1,
    } as ReportsFilters
    setReportsFilters(newFilters)

    if (newValues.reportType !== undefined) {
      if (String(newValues.reportType) === '1') {
        resetRoundsReports()
      } else {
        resetGlobalReports()
      }
    }

    formikReports.setValues({
      ...formikReports.values,
      ...newValues,
    } as any)

    return newFilters
  }

  const handleChangePage = (newPage: number) => {
    setReportsFilters({ ...filters, page: newPage })
    buildFiltersByReport({ ...filters, page: newPage })
  }

  const handleChangeLimit = (newLimit: number) => {
    if (filters.reportType === '1') {
      if (
        roundsPagination.page > Math.ceil(roundsPagination.total / newLimit)
      ) {
        setReportsFilters({ ...filters, limit: newLimit, page: 1 })
        buildFiltersByReport({ ...filters, limit: newLimit, page: 1 })
      } else {
        setReportsFilters({ ...filters, limit: newLimit })
        buildFiltersByReport({ ...filters, limit: newLimit })
      }
    } else {
      if (
        globalPagination.page > Math.ceil(globalPagination.total / newLimit)
      ) {
        setReportsFilters({ ...filters, limit: newLimit, page: 1 })
        buildFiltersByReport({ ...filters, limit: newLimit, page: 1 })
      } else {
        setReportsFilters({ ...filters, limit: newLimit })
        buildFiltersByReport({ ...filters, limit: newLimit })
      }
    }
  }

  /* TODO: Seems we will not use this code. Test in the future
   useEffect(() => {
    if (filters.reportType !== '' && filters.from !== 'playerCard') {
      submitSearchFilter()
    } 
  }, [brand]) // eslint-disable-line */

  return {
    filters,
    formikReports,
    submitSearchFilter,
    handleChangeSearch,
    handleBulkChangeSearch,
    handleChangePage,
    handleChangeLimit,
    downloadXlsxSearchFilter,
  }
}

export default useReportsFilters
